import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handles errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - logout user
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTH ENDPOINTS
// ============================================
export const authService = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (data) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  changePassword: async (data) => {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },
};

// ============================================
// MEMBER ENDPOINTS
// ============================================
export const memberService = {
  getCurrentMember: async () => {
    const response = await api.get('/members/me');
    return response.data;
  },

  getMember: async (id) => {
    const response = await api.get(`/members/${id}`);
    return response.data;
  },

  getAllMembers: async (params = {}) => {
    const response = await api.get('/members', { params });
    return response.data;
  },

  searchMembers: async (query, limit = 10) => {
    const response = await api.get('/members/search', { params: { q: query, limit } });
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/members/me', data);
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get('/members/me/settings');
    return response.data;
  },

  updateSettings: async (data) => {
    const response = await api.put('/members/me/settings', data);
    return response.data;
  },

  updateProfilePhoto: async (photoUrl) => {
    const response = await api.put('/members/me/profile-photo', { photoUrl });
    return response.data;
  },

  updateCoverPhoto: async (photoUrl) => {
    const response = await api.put('/members/me/cover-photo', { photoUrl });
    return response.data;
  },

  createManagedProfile: async (data) => {
    const response = await api.post('/members/managed-profile', data);
    return response.data;
  },

  updateManagedProfile: async (id, data) => {
    const response = await api.put(`/members/managed-profile/${id}`, data);
    return response.data;
  },
};

// ============================================
// POST ENDPOINTS
// ============================================
export const postService = {
  getNewsFeed: async (pageNumber = 1, pageSize = 20) => {
    const response = await api.get('/posts/feed', { params: { pageNumber, pageSize } });
    return response.data;
  },

  getPost: async (id) => {
    const response = await api.get(`/posts/${id}`);
    return response.data;
  },

  getMemberPosts: async (memberId, params = {}) => {
    const response = await api.get(`/posts/member/${memberId}`, { params });
    return response.data;
  },

  createPost: async (data) => {
    const response = await api.post('/posts', data);
    return response.data;
  },

  updatePost: async (id, data) => {
    const response = await api.put(`/posts/${id}`, data);
    return response.data;
  },

  deletePost: async (id) => {
    const response = await api.delete(`/posts/${id}`);
    return response.data;
  },

  pinPost: async (id, isPinned = true) => {
    const response = await api.post(`/posts/${id}/pin`, null, { params: { isPinned } });
    return response.data;
  },

  getPostComments: async (id) => {
    const response = await api.get(`/posts/${id}/comments`);
    return response.data;
  },

  getPostReactions: async (id) => {
    const response = await api.get(`/posts/${id}/reactions`, { params: { _t: Date.now() } });
    return response.data;
  },
};

// ============================================
// COMMENT ENDPOINTS
// ============================================
export const commentService = {
  createComment: async (data) => {
    const response = await api.post('/comments', data);
    return response.data;
  },

  updateComment: async (id, content) => {
    const response = await api.put(`/comments/${id}`, { content });
    return response.data;
  },

  deleteComment: async (id) => {
    const response = await api.delete(`/comments/${id}`);
    return response.data;
  },
};

// ============================================
// REACTION ENDPOINTS
// ============================================
export const reactionService = {
  getReactionTypes: async () => {
    const response = await api.get('/reactions/types');
    return response.data;
  },

  addReaction: async (data) => {
    const response = await api.post('/reactions', data);
    return response.data;
  },

  removePostReaction: async (postId) => {
    const response = await api.delete(`/reactions/post/${postId}`);
    return response.data;
  },

  removePhotoReaction: async (photoId) => {
    const response = await api.delete(`/reactions/photo/${photoId}`);
    return response.data;
  },

  removeCommentReaction: async (commentId) => {
    const response = await api.delete(`/reactions/comment/${commentId}`);
    return response.data;
  },
};

// ============================================
// PHOTO/ALBUM ENDPOINTS
// ============================================
export const photoService = {
  getMyPhotos: async (params = {}) => {
    const response = await api.get('/photos/me', { params });
    return response.data;
  },

  getMemberPhotos: async (memberId, params = {}) => {
    const response = await api.get(`/photos/member/${memberId}`, { params });
    return response.data;
  },

  getPhoto: async (id) => {
    const response = await api.get(`/photos/${id}`);
    return response.data;
  },

  uploadPhoto: async (formData) => {
    const response = await api.post('/photos/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  updatePhoto: async (id, data) => {
    const response = await api.put(`/photos/${id}`, data);
    return response.data;
  },

  deletePhoto: async (id) => {
    const response = await api.delete(`/photos/${id}`);
    return response.data;
  },

  // Albums
  getMyAlbums: async () => {
    const response = await api.get('/albums/me');
    return response.data;
  },

  getMemberAlbums: async (memberId) => {
    const response = await api.get(`/albums/member/${memberId}`);
    return response.data;
  },

  getAlbum: async (id) => {
    const response = await api.get(`/albums/${id}`);
    return response.data;
  },

  getAllMemberAlbums: async () => {
    const response = await api.get('/photos/albums/all');
    return response.data;
  },

  getEventAlbums: async () => {
    const response = await api.get('/photos/albums/events');
    return response.data;
  },

  getAlbumWithPhotos: async (id) => {
    const response = await api.get(`/albums/${id}/full`);
    return response.data;
  },

  createAlbum: async (data) => {
    const response = await api.post('/albums', data);
    return response.data;
  },

  updateAlbum: async (id, data) => {
    const response = await api.put(`/albums/${id}`, data);
    return response.data;
  },

  deleteAlbum: async (id) => {
    const response = await api.delete(`/photos/albums/${id}`);
    return response.data;
  },
};

// ============================================
// MESSAGING ENDPOINTS
// ============================================
export const messagingService = {
  getConversations: async () => {
    const response = await api.get('/conversations');
    return response.data;
  },

  getConversation: async (id) => {
    const response = await api.get(`/conversations/${id}`);
    return response.data;
  },

  createConversation: async (data) => {
    const response = await api.post('/conversations', data);
    return response.data;
  },

  getOrCreateDirectConversation: async (otherMemberId) => {
    const response = await api.post(`/conversations/direct/${otherMemberId}`);
    return response.data;
  },

  getMessages: async (conversationId, pageSize = 50, before = null) => {
    const params = { pageSize };
    if (before) params.before = before;
    const response = await api.get(`/conversations/${conversationId}/messages`, { params });
    return response.data;
  },

  sendMessage: async (data) => {
    const response = await api.post('/messages', data);
    return response.data;
  },

  markAsRead: async (conversationId) => {
    const response = await api.post(`/conversations/${conversationId}/read`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/conversations/unread-count');
    return response.data;
  },
};

// ============================================
// NOTIFICATION ENDPOINTS
// ============================================
export const notificationService = {
  getNotifications: async (pageNumber = 1, pageSize = 20) => {
    const response = await api.get('/notifications', { params: { pageNumber, pageSize } });
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  markAsRead: async (id) => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  deleteNotification: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },
};

// ============================================
// RELATIONSHIP ENDPOINTS
// ============================================
export const relationshipService = {
  getRelationshipTypes: async () => {
    const response = await api.get('/relationships/types');
    return response.data;
  },

  getMyRelationships: async () => {
    const response = await api.get('/relationships/me');
    return response.data;
  },

  getMemberRelationships: async (memberId) => {
    const response = await api.get(`/relationships/member/${memberId}`);
    return response.data;
  },

  createRelationship: async (data) => {
    const response = await api.post('/relationships', data);
    return response.data;
  },

  updateRelationship: async (id, data) => {
    const response = await api.put(`/relationships/${id}`, data);
    return response.data;
  },

  deleteRelationship: async (id) => {
    const response = await api.delete(`/relationships/${id}`);
    return response.data;
  },
};
// ============================================
// RECIPE ENDPOINTS
// ============================================
export const recipeService = {
  getAllRecipes: async (category = null, search = null) => {
    const params = {};
    if (category) params.category = category;
    if (search) params.search = search;
    const response = await api.get('/recipes', { params });
    return response.data;
  },

  getRecipe: async (id) => {
    const response = await api.get(`/recipes/${id}`);
    return response.data;
  },

  getCategories: async () => {
    const response = await api.get('/recipes/categories');
    return response.data;
  },

  createRecipe: async (data) => {
    const response = await api.post('/recipes', data);
    return response.data;
  },

  updateRecipe: async (id, data) => {
    const response = await api.put(`/recipes/${id}`, data);
    return response.data;
  },

  deleteRecipe: async (id) => {
    const response = await api.delete(`/recipes/${id}`);
    return response.data;
  },
};

// ============================================
// EVENT ENDPOINTS
// ============================================
export const eventService = {
  getUpcomingEvents: async () => {
    const response = await api.get('/events/upcoming');
    return response.data;
  },

  getPastEvents: async () => {
    const response = await api.get('/events/past');
    return response.data;
  },

  getEvent: async (id) => {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },

  createEvent: async (data) => {
    const response = await api.post('/events', data);
    return response.data;
  },

  updateEvent: async (id, data) => {
    const response = await api.put(`/events/${id}`, data);
    return response.data;
  },

  deleteEvent: async (id) => {
    const response = await api.delete(`/events/${id}`);
    return response.data;
  },

  rsvp: async (eventId, data) => {
    const response = await api.post(`/events/${eventId}/rsvp`, data);
    return response.data;
  },

  getComments: async (eventId) => {
    const response = await api.get(`/events/${eventId}/comments`);
    return response.data;
  },

  addComment: async (eventId, data) => {
    const response = await api.post(`/events/${eventId}/comments`, data);
    return response.data;
  },

  deleteComment: async (commentId) => {
    const response = await api.delete(`/events/comments/${commentId}`);
    return response.data;
  },

  getOrCreateAlbum: async (eventId) => {
    const response = await api.post(`/events/${eventId}/album`);
    return response.data;
  },

  getEventPhotos: async (eventId) => {
    const response = await api.get(`/events/${eventId}/photos`);
    return response.data;
  },
};

// ============================================
// STORY ENDPOINTS
// ============================================
export const storyService = {
  getAllStories: async () => {
    const response = await api.get('/stories');
    return response.data;
  },

  getStoriesByMember: async (memberId) => {
    const response = await api.get(`/stories/member/${memberId}`);
    return response.data;
  },

  getStory: async (id) => {
    const response = await api.get(`/stories/${id}`);
    return response.data;
  },

  createStory: async (data) => {
    const response = await api.post('/stories', data);
    return response.data;
  },

  updateStory: async (id, data) => {
    const response = await api.put(`/stories/${id}`, data);
    return response.data;
  },

  deleteStory: async (id) => {
    const response = await api.delete(`/stories/${id}`);
    return response.data;
  },
};

export default api;
